const galleryState = new WeakMap();

function getVisibleThumbCount(container, thumbs) {
    if (!container || !thumbs?.length) return 1;

    const styles = window.getComputedStyle(container);
    const gap =
        parseFloat(styles.columnGap || styles.gap || "0") || 0;
    const thumbWidth = thumbs[0]?.offsetWidth || 0;

    if (!thumbWidth) return 1;

    return Math.max(
        1,
        Math.floor((container.clientWidth + gap) / (thumbWidth + gap)),
    );
}

function scrollThumbIntoViewHorizontally(container, thumbs, activeIndex) {
    if (!container || !thumbs?.length) return;

    const safeIndex = Math.max(0, Math.min(activeIndex, thumbs.length - 1));
    const visibleCount = getVisibleThumbCount(container, thumbs);

    // Делаем выбранную миниатюру первой видимой
    let firstVisibleIndex = safeIndex;

    // Если дошли до конца, листаем в край, чтобы не было пустоты справа
    const maxFirstVisibleIndex = Math.max(0, thumbs.length - visibleCount);
    if (firstVisibleIndex > maxFirstVisibleIndex) {
        firstVisibleIndex = maxFirstVisibleIndex;
    }

    const targetThumb = thumbs[firstVisibleIndex];
    if (!targetThumb) return;

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const targetLeft = Math.min(targetThumb.offsetLeft, maxScrollLeft);

    container.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: "smooth",
    });
}

function safeParseJSON(text, fallback = null) {
    try {
        return JSON.parse(text);
    } catch (error) {
        return fallback;
    }
}

function normalizeValue(value) {
    return String(value ?? "").trim();
}

function getScopedRoot(gallery) {
    return (
        gallery.closest(".shopify-section") ||
        gallery.closest("[id^='shopify-section-']") ||
        gallery.closest("section") ||
        document
    );
}

function getVariantData(gallery) {
    const productId = gallery.dataset.productId;
    if (!productId) return [];

    const el = document.getElementById(`pi-variant-data-${productId}`);
    if (!el) return [];

    return safeParseJSON(el.textContent, []) || [];
}

function getMasterVariantField(root) {
    const selectors = [
        'form[action*="/cart/add"] input[name="id"]',
        'form[action*="/cart/add"] select[name="id"]',
        'product-form form input[name="id"]',
        'product-form form select[name="id"]',
        'input[name="id"]',
        'select[name="id"]',
    ];

    for (const selector of selectors) {
        const field = root.querySelector(selector);
        if (field) return field;
    }

    return null;
}

function getVariantIdFromDom(root) {
    const masterField = getMasterVariantField(root);
    if (masterField && masterField.value) {
        return normalizeValue(masterField.value);
    }

    const explicitSelected = root.querySelector(
        '[data-variant-id].is-selected, [data-variant-id][aria-selected="true"], [data-variant-id][aria-checked="true"]',
    );
    if (explicitSelected) {
        return normalizeValue(explicitSelected.dataset.variantId);
    }

    const urlVariant = new URLSearchParams(window.location.search).get(
        "variant",
    );
    if (urlVariant) {
        return normalizeValue(urlVariant);
    }

    return null;
}

function collectSelectedOptions(root) {
    const values = [];

    const groups = root.querySelectorAll(
        "fieldset, .product-form__input, variant-radios, variant-selects",
    );

    if (groups.length) {
        groups.forEach((group) => {
            const checkedRadio = group.querySelector(
                "input[type='radio']:checked",
            );
            const selectedSelect = group.querySelector("select");

            if (checkedRadio) {
                const name = normalizeValue(checkedRadio.name).toLowerCase();
                if (name !== "id" && name !== "quantity") {
                    values.push(normalizeValue(checkedRadio.value));
                }
                return;
            }

            if (selectedSelect) {
                const name = normalizeValue(selectedSelect.name).toLowerCase();
                if (name !== "id" && name !== "quantity") {
                    values.push(normalizeValue(selectedSelect.value));
                }
            }
        });
    }

    if (values.length) {
        return values.filter(Boolean).slice(0, 3);
    }

    const checkedRadios = [
        ...root.querySelectorAll("input[type='radio']:checked"),
    ];
    checkedRadios.forEach((input) => {
        const name = normalizeValue(input.name).toLowerCase();
        if (name !== "id" && name !== "quantity") {
            values.push(normalizeValue(input.value));
        }
    });

    const selects = [...root.querySelectorAll("select")];
    selects.forEach((select) => {
        const name = normalizeValue(select.name).toLowerCase();
        if (name !== "id" && name !== "quantity") {
            values.push(normalizeValue(select.value));
        }
    });

    return values.filter(Boolean).slice(0, 3);
}

function findVariantFromState(gallery) {
    const root = getScopedRoot(gallery);
    const variantData = getVariantData(gallery);
    if (!variantData.length) return null;

    const variantId = getVariantIdFromDom(root);
    if (variantId) {
        const byId = variantData.find(
            (variant) => normalizeValue(variant.id) === variantId,
        );
        if (byId) return byId;
    }

    const selectedOptions = collectSelectedOptions(root);
    if (!selectedOptions.length) return null;

    return (
        variantData.find((variant) => {
            return selectedOptions.every((value, index) => {
                return (
                    normalizeValue(variant[`option${index + 1}`]) ===
                    normalizeValue(value)
                );
            });
        }) || null
    );
}

function initGallery(gallery) {
    const existingState = galleryState.get(gallery);
    if (existingState?.destroy) {
        existingState.destroy();
    }

    const scroller =
        gallery.querySelector("slideshow-slides") ||
        gallery.querySelector("slideshow-container") ||
        gallery.querySelector("slideshow-component");

    const slides = [...gallery.querySelectorAll("slideshow-slide")];
    const thumbs = [...gallery.querySelectorAll(".pi-product-thumb")];
    const thumbsContainer = gallery.querySelector(".pi-product-thumbs");

    if (!scroller || !slides.length || !thumbs.length || !thumbsContainer)
        return;

    const cleanup = [];
    let lastVariantId = null;

    const setActiveThumb = (index, shouldScroll = false) => {
    thumbs.forEach((thumb, i) => {
        thumb.classList.toggle("is-active", i === index);
    });

    if (shouldScroll && thumbs[index]) {
        scrollThumbIntoViewHorizontally(thumbsContainer, thumbs, index);
    }
};

    const goToSlide = (index, smooth = true) => {
        // Получаем свежий массив слайдов на случай изменений в DOM
        const currentSlides = [...gallery.querySelectorAll("slideshow-slide")];
        const slide = currentSlides[index];
        if (!slide) return;

        // Ищем фактический контейнер скролла
        const scrollContainer =
            slide.closest("slideshow-slides") || slide.parentElement;

        if (
            scrollContainer &&
            scrollContainer.scrollWidth > scrollContainer.clientWidth
        ) {
            // Идеально точный расчет координаты для iOS
            const containerRect = scrollContainer.getBoundingClientRect();
            const slideRect = slide.getBoundingClientRect();

            const targetLeft =
                scrollContainer.scrollLeft +
                (slideRect.left - containerRect.left);

            scrollContainer.scrollTo({
                left: targetLeft,
                behavior: smooth ? "smooth" : "auto",
            });
        } else {
            // Фолбэк
            slide.scrollIntoView({
                behavior: smooth ? "smooth" : "auto",
                inline: "start",
                block: "nearest",
            });
        }

        setActiveThumb(index, true);
    };

    const goToMediaId = (mediaId, smooth = true) => {
        if (!mediaId) return;

        const index = thumbs.findIndex(
            (thumb) =>
                normalizeValue(thumb.dataset.mediaId) ===
                normalizeValue(mediaId),
        );

        if (index === -1) return;
        goToSlide(index, smooth);
    };

    thumbs.forEach((thumb, index) => {
        const handleClick = (event) => {
            event.preventDefault();
            goToSlide(index, true);
        };

        thumb.addEventListener("click", handleClick);
        cleanup.push(() => thumb.removeEventListener("click", handleClick));
    });

    const intersectionObserver = new IntersectionObserver(
        (entries) => {
            const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

            if (!visible) return;

            const index = slides.indexOf(visible.target);
            if (index !== -1) {
                setActiveThumb(index, true);
            }
        },
        {
            root: scroller,
            threshold: 0.6,
        },
    );

    slides.forEach((slide) => intersectionObserver.observe(slide));

    const syncToCurrentVariant = (smooth = true) => {
        const variant = findVariantFromState(gallery);
        if (!variant) return;

        const currentVariantId = normalizeValue(variant.id);
        if (currentVariantId && currentVariantId === lastVariantId) return;

        lastVariantId = currentVariantId;

        if (variant.featured_media_id) {
            goToMediaId(variant.featured_media_id, smooth);
        }
    };

    const handleFormChange = () => {
        requestAnimationFrame(() => {
            setTimeout(() => syncToCurrentVariant(true), 0);
        });
    };

    const handleVariantEvent = (event) => {
        const detail = event.detail || {};
        const variant =
            detail.variant ||
            detail.currentVariant ||
            detail.productVariant ||
            null;

        if (variant?.id) {
            lastVariantId = normalizeValue(variant.id);

            const mediaId =
                variant.featured_media?.id || variant.featured_media_id || null;

            if (mediaId) {
                goToMediaId(mediaId, true);
                return;
            }
        }

        syncToCurrentVariant(true);
    };

    document.addEventListener("change", handleFormChange, true);
    document.addEventListener("input", handleFormChange, true);
    document.addEventListener("variant:change", handleVariantEvent, true);
    document.addEventListener("variantChange", handleVariantEvent, true);
    document.addEventListener(
        "product:variant-change",
        handleVariantEvent,
        true,
    );

    cleanup.push(() =>
        document.removeEventListener("change", handleFormChange, true),
    );
    cleanup.push(() =>
        document.removeEventListener("input", handleFormChange, true),
    );
    cleanup.push(() =>
        document.removeEventListener(
            "variant:change",
            handleVariantEvent,
            true,
        ),
    );
    cleanup.push(() =>
        document.removeEventListener("variantChange", handleVariantEvent, true),
    );
    cleanup.push(() =>
        document.removeEventListener(
            "product:variant-change",
            handleVariantEvent,
            true,
        ),
    );

    const variantPoller = window.setInterval(() => {
        syncToCurrentVariant(false);
    }, 250);

    cleanup.push(() => window.clearInterval(variantPoller));

    const initiallyActiveIndex = thumbs.findIndex((thumb) =>
        thumb.classList.contains("is-active"),
    );
    setActiveThumb(
        initiallyActiveIndex !== -1 ? initiallyActiveIndex : 0,
        false,
    );

    syncToCurrentVariant(false);

    galleryState.set(gallery, {
        destroy() {
            intersectionObserver.disconnect();
            cleanup.forEach((fn) => fn());
        },
    });
}

function initAllGalleries() {
    document.querySelectorAll(".pi-media-gallery").forEach(initGallery);
}

document.addEventListener("DOMContentLoaded", () => {
    initAllGalleries();

    const mutationObserver = new MutationObserver(() => {
        initAllGalleries();
    });

    mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
    });
});
